import type { Worksheet } from "exceljs";
import type { Department } from "../../../contexts/AuthProvider";
import type { HospitalData } from "../../../utils/models";
import type { PrintoutColumn, PrintoutColumnType, RowNoteFlags, ScheduleRow } from "../types";
import { applyRowDecoration, populateCell } from "./cellPopulator";
import { THIN_BORDER } from "./cellStyle";
import { composeMergeGroupKey, orderMergeColumns, sortRowsByMergeColumns } from "./mergeColumnSort";
import { composeRowNotes } from "./rowNotes";

interface RenderStandardSectionRowsArgs {
	worksheet: Worksheet;
	rows: ScheduleRow[];
	currentRow: number;
	enabledColumns: PrintoutColumn[];
	expandedColumns: PrintoutColumn[];
	hospitalData: HospitalData;
	department: Department;
	timezone: string;
	jobManagementEnabled: boolean;
	printNotes: boolean;
	staffPerRow: number;
	rowGroupingBy: PrintoutColumnType[];
	noteFlags: RowNoteFlags | undefined;
	sortBy: PrintoutColumn[] | undefined;
	customStringOrderMap: Map<string, number> | null | undefined;
	appendRoleToName: boolean;
}

/**
 * Emit the data rows for one section using the standard layout (mapping §8.1).
 *
 * Two paths:
 * - `staffPerRow === 1` → one ScheduleRow per Excel row. Pre-sort by merge columns so that the
 *   downstream `applyVerticalMerges` can collapse adjacent same-value cells into one.
 * - `staffPerRow > 1` → pack up to N rows into each Excel row. **Never** pack across a merge-column
 *   boundary — if the merge key changes mid-batch, the batch closes and a new Excel row starts
 *   with empty trailing slots if needed.
 *
 * Returns the next row number after the section's data.
 */
export const renderStandardSectionRows = (args: RenderStandardSectionRowsArgs): number => {
	const {
		worksheet,
		rows,
		currentRow: initialRow,
		enabledColumns,
		expandedColumns,
		hospitalData,
		department,
		timezone,
		jobManagementEnabled,
		printNotes,
		staffPerRow,
		rowGroupingBy,
		noteFlags,
		sortBy,
		customStringOrderMap,
		appendRoleToName,
	} = args;

	let currentRow = initialRow;
	const orderedMergeCols = orderMergeColumns(rowGroupingBy, enabledColumns);
	const rowsToProcess =
		orderedMergeCols.length > 0
			? sortRowsByMergeColumns({ rows, mergeCols: orderedMergeCols, enabledColumns, sortBy, customStringOrderMap, jobManagementEnabled })
			: rows;

	const hasNotesColumn = enabledColumns.some((col) => col.type === "notes");
	const printsTrailingNotes = printNotes && !hasNotesColumn;

	if (staffPerRow <= 1) {
		for (const row of rowsToProcess) {
			const excelRow = worksheet.getRow(currentRow);
			enabledColumns.forEach((col, idx) => {
				populateCell({ cell: excelRow.getCell(idx + 1), col, row, hospitalData, timezone, department, jobManagementEnabled, noteFlags, appendRoleToName });
			});
			if (printsTrailingNotes) {
				excelRow.getCell(enabledColumns.length + 1).value = composeRowNotes({ nurse: row.nurse, hospitalData, flags: noteFlags });
			}
			const maxCol = enabledColumns.length + (printsTrailingNotes ? 1 : 0);
			applyRowDecoration({ row: excelRow, scheduleRow: row, maxColumn: maxCol });
			currentRow += 1;
		}
		return currentRow;
	}

	// staffPerRow > 1: pack rows respecting merge-column boundaries
	const mergeColCount = enabledColumns.filter((col) => rowGroupingBy.includes(col.type)).length;
	const nonMergePerStaff = mergeColCount > 0 ? (expandedColumns.length - mergeColCount) / staffPerRow : expandedColumns.length / staffPerRow;

	let i = 0;
	while (i < rowsToProcess.length) {
		const firstKey = orderedMergeCols.length > 0 ? composeMergeGroupKey(rowsToProcess[i], orderedMergeCols, jobManagementEnabled) : null;
		let batchEnd = i + 1;
		while (
			batchEnd < rowsToProcess.length &&
			batchEnd < i + staffPerRow &&
			(firstKey === null || composeMergeGroupKey(rowsToProcess[batchEnd], orderedMergeCols, jobManagementEnabled) === firstKey)
		) {
			batchEnd++;
		}

		const batch = rowsToProcess.slice(i, batchEnd);
		const excelRow = worksheet.getRow(currentRow);
		const firstRow = batch[0];

		// Merge columns appear once on the left, populated from the batch's first row.
		// Use `expandedColumns` (which is `[...mergeColumns, ...nonMergeColumns × staffPerRow]`)
		// directly — indexing into `enabledColumns` is wrong because that array reflects the
		// user's chosen column order, which may interleave merge and non-merge columns.
		for (let m = 0; m < mergeColCount; m++) {
			const col = expandedColumns[m];
			const cell = excelRow.getCell(m + 1);
			populateCell({ cell, col, row: firstRow, hospitalData, timezone, department, jobManagementEnabled, noteFlags, appendRoleToName });
			cell.border = THIN_BORDER;
		}

		// Non-merge columns repeated per staff slot
		for (let s = 0; s < staffPerRow; s++) {
			const row = batch[s];
			const staffOffset = mergeColCount + s * nonMergePerStaff;
			for (let c = 0; c < nonMergePerStaff; c++) {
				const col = expandedColumns[staffOffset + c];
				const cell = excelRow.getCell(staffOffset + c + 1);
				populateCell({ cell, col, row, hospitalData, timezone, department, jobManagementEnabled, noteFlags, appendRoleToName });
				cell.border = THIN_BORDER;
			}
		}

		if (printsTrailingNotes) {
			const notesCell = excelRow.getCell(expandedColumns.length + 1);
			notesCell.value = composeRowNotes({ nurse: batch[0]?.nurse, hospitalData, flags: noteFlags });
			notesCell.border = THIN_BORDER;
		}

		currentRow += 1;
		i = batchEnd;
	}

	return currentRow;
};
